import { Injectable, inject } from '@angular/core';
import FroalaEditor from 'froala-editor';
import {
    FroalaEditorOptions,
    FroalaImageUploadHandlers,
    ImageAssetRef,
    ImageMeta,
} from '@r1-compass-library/models/froala/froala-editor-options';
import { FroalaEvent } from '@r1-compass-library/models/froala/froala-event';
import { FieldMergeConstants } from '@r1-compass-library/constants/field-merge.constants';
import { LocalStorageService } from '@r1-compass-library/services/local-storage.service';
import { FroalaImagesService } from './froala-images.service';

@Injectable({
    providedIn: 'root',
})
export class FroalaImageUploadService {
    readonly #localStorageService = inject(LocalStorageService);
    readonly #froalaImagesService = inject(FroalaImagesService);

    public attachImageUploadBehavior(froalaOptions: FroalaEditorOptions, handlers: FroalaImageUploadHandlers): void {
        const imagesService = this.#froalaImagesService;
        const existingPasteAfter = froalaOptions.events?.['paste.after'];
        const existingPasteAfterCleanup = froalaOptions.events?.['paste.afterCleanup'];
        const existingPasteBeforeCleanup = froalaOptions.events?.['paste.beforeCleanup'];
        const clearCopyCache = () => this.#clearFroalaCopyCache();

        froalaOptions.events = {
            ...froalaOptions.events,
            'image.removed': () => {
                handlers.onImageRemoved?.();
            },
            'image.beforePasteUpload': function (img: HTMLImageElement) {
                // If it already belongs to the current asset, do not re-upload
                const currentAssetId = handlers.getAssetId?.();
                const currentAssetType = handlers.getAssetType?.() ?? 'play';
                const src = img?.getAttribute?.(FieldMergeConstants.SRC) || '';
                const hasOptions = img?.getAttribute?.(FieldMergeConstants.DATA_CONTENT_OPTIONS);

                if (hasOptions && currentAssetId) {
                    const isCurrentAsset =
                        src.includes(`/${currentAssetType}s/${currentAssetId}/`) || imagesService.isKnownBlobUrl(src);
                    if (isCurrentAsset) {
                        img.removeAttribute(FieldMergeConstants.FR_PASTED_IMAGE);
                        return false;
                    }
                }
                return true;
            },
            'image.beforeUpload': function (this: FroalaEditor, files: FileList | Blob[], img?: unknown) {
                if (!files || !files.length) {
                    return false;
                }

                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const editor = this;
                const rawFile = files[0];
                const resolvedEl = imagesService.resolveImageElement(img);
                const extractedName = resolvedEl ? imagesService.extractFileNameFromElement(resolvedEl) : null;
                const fileName = imagesService.resolveImageFileName(rawFile, extractedName);
                const file =
                    rawFile instanceof File && rawFile.name === fileName
                        ? rawFile
                        : new File([rawFile], fileName, { type: rawFile.type || 'image/png' });
                const meta = { fileName, ...handlers.getAssetRef() };

                handlers.uploadImage$(file, fileName).subscribe({
                    next: res => {
                        const localBlobUrl = URL.createObjectURL(file);
                        imagesService.trackBlobUrl(localBlobUrl);

                        // 1. Update existing pasted element in-place or insert standard HTML
                        if (!imagesService.updatePastedImage(img, localBlobUrl, meta)) {
                            const html = handlers.buildImageHtml
                                ? handlers.buildImageHtml(localBlobUrl, fileName)
                                : imagesService.buildImageElementHtml(localBlobUrl, meta);
                            editor.html.insert(html);
                        }

                        // 2. Standard Froala Lifecycle
                        editor.popups?.hideAll?.();
                        editor.undo?.saveStep();
                        editor.events.trigger(FroalaEvent.ContentChanged, []);

                        // 3. Notify caller
                        handlers.onUploadSuccess?.(fileName);
                    },
                    error: err => {
                        imagesService.removePastedImage(img);
                        editor.popups?.hideAll?.();
                        editor.events.trigger(FroalaEvent.ContentChanged, []);
                        handlers.onUploadError?.(fileName, err);
                    },
                });

                // Prevents Froala's default synchronous HTTP POST
                return false;
            },
            'paste.after': function (this: FroalaEditor) {
                if (typeof existingPasteAfter === 'function') {
                    existingPasteAfter.call(this);
                }

                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const editor = this;
                const currentAssetId = handlers.getAssetId?.();
                const currentAssetType = handlers.getAssetType?.() ?? 'play';

                const container = editor.$el?.[0] as HTMLElement | undefined;
                if (!container) {
                    return;
                }

                const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
                images.forEach(img => {
                    if (img.hasAttribute('data-uploading') || img.hasAttribute('data-fr-image-pasted')) {
                        return;
                    }

                    const src = img.getAttribute('src') || '';
                    if (!src || src.includes('data:image/svg+xml') || src.includes('{{Image.')) {
                        return;
                    }

                    const isDataUri = src.startsWith('data:');
                    const isForeignAsset =
                        Boolean(currentAssetId) &&
                        src.includes(`/${currentAssetType}s/`) &&
                        !src.includes(`/${currentAssetType}s/${currentAssetId}/`);
                    const isForeignBlob = src.startsWith('blob:') && !imagesService.isKnownBlobUrl(src);

                    if (isDataUri || isForeignAsset || isForeignBlob) {
                        img.setAttribute('data-uploading', 'true');
                        const extractedName = imagesService.extractFileNameFromElement(img);

                        fetch(src)
                            .then(res => res.blob())
                            .then(blob => {
                                const fileName = imagesService.resolveImageFileName(blob, extractedName);
                                const file = new File([blob], fileName, { type: blob.type || 'image/png' });
                                const meta = { fileName, ...handlers.getAssetRef() };

                                handlers.uploadImage$(file, fileName).subscribe({
                                    next: () => {
                                        const localBlobUrl = URL.createObjectURL(file);
                                        imagesService.trackBlobUrl(localBlobUrl);
                                        imagesService.updatePastedImage(img, localBlobUrl, meta);
                                        editor.undo?.saveStep();
                                        editor.events.trigger(FroalaEvent.ContentChanged, []);
                                        handlers.onUploadSuccess?.(fileName);
                                    },
                                    error: err => {
                                        img.removeAttribute('data-uploading');
                                        handlers.onUploadError?.(fileName, err);
                                    },
                                });
                            })
                            .catch(() => {
                                img.removeAttribute('data-uploading');
                            });
                    }
                });
            },
            'paste.beforeCleanup': function (this: FroalaEditor, html: string) {
                if (typeof existingPasteBeforeCleanup === 'function') {
                    existingPasteBeforeCleanup.call(this, html);
                }
                // Park here, not only in afterCleanup: between the two, Froala's paste cleanup puts the HTML into a
                // live <div> (paste module H()), and that starts a download of every <img src> it contains.
                return parkForeignImagesInHtml(html);
            },
            'paste.afterCleanup': function (this: FroalaEditor, html: string) {
                if (typeof existingPasteAfterCleanup === 'function') {
                    existingPasteAfterCleanup.call(this, html);
                }
                return parkForeignImagesInHtml(html);
            },
        };

        const root = (editor: FroalaEditor) => editor.el as HTMLElement;
        // Copies made by this editor, keyed "<target asset>|<source asset>/<source fileName>". One upload per source image.
        const copies = new Map<string, { fileName: string; blobUrl?: string; waiting: HTMLImageElement[] }>();
        const existingInitialized = froalaOptions.events?.['initialized'];

        froalaOptions.events = {
            ...froalaOptions.events,
            initialized: function (this: FroalaEditor) {
                if (typeof existingInitialized === 'function') {
                    existingInitialized.call(this);
                }
                const editor = this;
                editor.events.on(FroalaEvent.PasteBefore, (e: ClipboardEvent) => onPasteBefore(editor, e), true); // first position
                editor.events.on(FroalaEvent.PasteAfter, () => rehomeForeignImages(editor));
            },
        };

        /** Returns true when the clipboard HTML is empty or is just a single <img>, so the bytes are the content. */
        const htmlIsJustOneImage = (html: string): boolean => {
            if (!html || /<!--StartFragment-->\s*<!--EndFragment-->/.test(html)) {
                return true;
            }
            const body = new DOMParser().parseFromString(html, 'text/html').body;
            return body.querySelectorAll('img').length === 1 && !body.textContent?.trim();
        };

        /** Chrome's "Copy Image" HTML has only src (+ alt). Rebuild the meta from the S3 URL path. */
        function metaFromS3Img(img: HTMLImageElement | null): ImageMeta | null {
            const src = img?.getAttribute(FieldMergeConstants.SRC) ?? '';
            const ref = imagesService.parseAssetRefFromS3Url(src);
            if (!ref) return null;
            const fileName = decodeURIComponent(new URL(src).pathname.split('/').pop() ?? '');
            return fileName ? { fileName, ...ref } : null;
        }

        const sourceRefOf = (meta: ImageMeta, src: string): ImageAssetRef | null =>
            meta.assetId && meta.assetType
                ? { assetId: meta.assetId, assetType: meta.assetType }
                : imagesService.parseAssetRefFromS3Url(src);

        /** Foreign image: move its URL to data-copy-src so the browser doesn't download it. rehome fetches it once. */
        function parkForeignSrc(img: HTMLImageElement, ref: ImageAssetRef): void {
            const meta = imagesService.readImageMeta(img);
            const src = img.getAttribute(FieldMergeConstants.SRC) || '';
            const source = meta ? sourceRefOf(meta, src) : null;
            if (!meta || !source || img.hasAttribute(FieldMergeConstants.COPY_SRC)) {
                return; // no meta, or already parked (paste.beforeCleanup runs before paste.afterCleanup)
            }
            if (source.assetId === ref.assetId && source.assetType === ref.assetType) {
                // local: same URL already on screen, loaded by hydrate with crossorigin. Without the same CORS mode
                // Firefox can't reuse that copy and downloads the image again.
                img.setAttribute('crossorigin', 'anonymous');
                return;
            }

            img.setAttribute(FieldMergeConstants.COPY_SRC, src);
            img.setAttribute(FieldMergeConstants.SRC, imagesService.buildLoadingImageSvgDataUrl(meta.fileName));
            img.removeAttribute(FieldMergeConstants.FR_PASTED_IMAGE); // else Froala's image plugin (paste.after -> ne) uploads the placeholder
        }

        /** paste.afterCleanup is a chain trigger: return the new HTML, or undefined to leave it unchanged. */
        function parkForeignImagesInHtml(html: string): string | undefined {
            if (typeof html !== 'string' || !html.includes(FieldMergeConstants.DATA_CONTENT_OPTIONS)) {
                return undefined;
            }
            const ref = handlers.getAssetRef();
            const tpl = document.createElement('template'); // inert: parsing here doesn't start image downloads
            tpl.innerHTML = html;
            tpl.content
                .querySelectorAll<HTMLImageElement>(`img[${FieldMergeConstants.DATA_CONTENT_OPTIONS}]`)
                .forEach(img => {
                    // rehome owns images with meta (same asset: keep, foreign: copy). Without this, the image
                    // plugin's paste.after re-encodes blob:/data: srcs to PNG and uploads them over the same name.
                    img.removeAttribute(FieldMergeConstants.FR_PASTED_IMAGE);
                    parkForeignSrc(img, ref);
                });
            // Raw clipboard HTML (paste.beforeCleanup) has <html><body>. Keep it: Froala's body branch normalizes newlines.
            return /<body/i.test(html) ? `<html><body>${tpl.innerHTML}</body></html>` : tpl.innerHTML;
        }

        function onPasteBefore(editor: FroalaEditor, e: ClipboardEvent): boolean | void {
            const cd = e?.clipboardData;
            if (!cd) {
                return;
            }

            const file = Array.from(cd.items ?? [])
                .find(i => i.kind === 'file' && i.type.startsWith('image/'))
                ?.getAsFile();
            const html = cd.getData('text/html') || '';

            // No bytes, or rich HTML (Word/Excel also put a PNG on the clipboard): let Froala handle it.
            if (!file || !htmlIsJustOneImage(html)) {
                return;
            }

            const srcImg = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('img') : null;
            const srcMeta = imagesService.readImageMeta(srcImg) ?? metaFromS3Img(srcImg);
            if (srcImg && srcMeta && !srcImg.hasAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS)) {
                srcImg.setAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS, JSON.stringify(srcMeta));
            }
            const ref = handlers.getAssetRef();

            // Bytes win. Clear Froala's Firefox fallback cache so it can't paste a stale image later.
            clearCopyCache();
            e.preventDefault();
            e.stopPropagation();

            // Copied out of a Froala editor (Firefox "Copy Image" sends the <img> HTML AND re-encoded PNG bytes).
            // Ignore the bytes and take the Ctrl+C route: same asset reuses the object, foreign gets the original file copied.
            // Don't just `return` here. Froala's image plugin pe() would see the file and upload the PNG as a new image.
            if (srcImg && srcMeta) {
                parkForeignSrc(srcImg, ref); // no-op for same asset
                editor.undo.saveStep();
                editor.html.insert(srcImg.outerHTML);
                editor.events.trigger(FroalaEvent.PasteAfter, []); // same as Froala's image plugin: runs rehome + markInteracted
                editor.undo.saveStep(); // AFTER the insert: fires contentChanged, so the FormControl gets the new <img>
                return false;
            }

            const fileName = imagesService.uniqueFileName(
                imagesService.resolveImageFileName(file, srcImg?.getAttribute('alt')),
                usedNames(editor)
            );
            insertAndUpload(editor, file, fileName, ref);
            return false; // core paste and the image plugin's pe() never run
        }

        function insertAndUpload(editor: FroalaEditor, file: File, fileName: string, ref: ImageAssetRef): void {
            const uploadId = crypto.randomUUID();
            const blobUrl = URL.createObjectURL(file);
            imagesService.trackBlobUrl(blobUrl); // else the legacy paste.after sees an unknown blob: on the next paste and uploads it again

            editor.undo.saveStep();
            // Insert synchronously, while the caret is still where the user pasted.
            editor.html.insert(
                imagesService
                    .buildImageElementHtml(blobUrl, { fileName, ...ref })
                    .replace('<img ', `<img data-upload-id="${uploadId}" data-uploading="true" `)
            );
            const find = () => root(editor).querySelector<HTMLImageElement>(`img[data-upload-id="${uploadId}"]`);

            handlers.uploadImage$(new File([file], fileName, { type: file.type }), fileName).subscribe({
                next: () => {
                    const img = find();
                    img?.removeAttribute('data-upload-id');
                    img?.removeAttribute('data-uploading');
                    editor.undo.saveStep();
                    editor.events.trigger(FroalaEvent.ContentChanged, []);
                    handlers.onUploadSuccess?.(fileName);
                },
                error: err => {
                    find()?.remove();
                    URL.revokeObjectURL(blobUrl);
                    editor.events.trigger(FroalaEvent.ContentChanged, []);
                    handlers.onUploadError?.(fileName, err);
                },
            });
        }

        function usedNames(editor: FroalaEditor, except?: HTMLImageElement): Set<string> {
            const names = new Set<string>();
            root(editor)
                .querySelectorAll<HTMLImageElement>(`img[${FieldMergeConstants.DATA_CONTENT_OPTIONS}]`)
                .forEach(i => {
                    if (
                        i === except ||
                        (i.hasAttribute(FieldMergeConstants.COPY_SRC) && !i.hasAttribute('data-uploading'))
                    ) {
                        return; // the image itself, or one still parked from this paste (it has no name here yet)
                    }
                    const m = imagesService.readImageMeta(i);
                    if (m) {
                        names.add(m.fileName);
                    }
                });
            return names;
        }

        /** Runs after every paste. Copies any <img> owned by another asset into this one. */
        function rehomeForeignImages(editor: FroalaEditor): void {
            const ref = handlers.getAssetRef();
            const imgs = root(editor).querySelectorAll<HTMLImageElement>(
                `img[${FieldMergeConstants.DATA_CONTENT_OPTIONS}]:not([data-uploading])`
            );

            imgs.forEach(img => {
                const meta = imagesService.readImageMeta(img);
                if (!meta) {
                    return;
                }

                // Parked by parkForeignSrc (src is now the placeholder SVG). Otherwise use the live src.
                const pastedSrc = img.getAttribute(FieldMergeConstants.COPY_SRC) || img.src;
                const source = sourceRefOf(meta, pastedSrc);

                const isLocal = source?.assetId === ref.assetId && source?.assetType === ref.assetType;
                if (isLocal) {
                    // Same-asset paste: only restore the classes pasteDeniedAttrs stripped.
                    imagesService.writeImageMeta(img, { fileName: meta.fileName, ...ref });
                    return;
                }
                if (!source) {
                    img.removeAttribute(FieldMergeConstants.COPY_SRC);
                    markImageBroken(img, meta.fileName);
                    return;
                }

                // Same source image already copied (or copying) into this asset: show that file again, no second upload.
                const key = `${ref.assetType}/${ref.assetId}|${source.assetType}/${source.assetId}/${meta.fileName}`;
                const existing = copies.get(key);
                if (existing && usedNames(editor, img).has(existing.fileName)) {
                    imagesService.writeImageMeta(img, { fileName: existing.fileName, ...ref });
                    if (existing.blobUrl) {
                        showImageCopy(img, existing.blobUrl, existing.fileName, ref);
                    } else {
                        img.setAttribute('data-uploading', 'true');
                        existing.waiting.push(img);
                    }
                    return;
                }

                const targetName = imagesService.uniqueFileName(meta.fileName, usedNames(editor, img));
                img.setAttribute('data-uploading', 'true');
                // Claim the name now, so the next image in this paste (and the next paste) can't pick it too.
                imagesService.writeImageMeta(img, { fileName: targetName, ...ref });
                const copy = { fileName: targetName, waiting: [img] };
                copies.set(key, copy);

                handlers
                    .copyImageToCurrentAsset$({ ...source, fileName: meta.fileName }, targetName, pastedSrc)
                    .subscribe({
                        next: file => {
                            // Show the bytes we already have. No presigned GET + re-download of what we just uploaded.
                            const blobUrl = URL.createObjectURL(file);
                            imagesService.trackBlobUrl(blobUrl);
                            copies.set(key, { ...copy, blobUrl, waiting: [] });
                            copy.waiting.forEach(i => showImageCopy(i, blobUrl, targetName, ref));
                            editor.undo.saveStep();
                            editor.events.trigger(FroalaEvent.ContentChanged, []);
                            handlers.onUploadSuccess?.(targetName);
                        },
                        error: err => {
                            copies.delete(key);
                            copy.waiting.forEach(i => {
                                i.removeAttribute('data-uploading');
                                i.removeAttribute(FieldMergeConstants.COPY_SRC);
                                markImageBroken(i, meta.fileName);
                            });
                            editor.events.trigger(FroalaEvent.ContentChanged, []);
                            handlers.onUploadError?.(meta.fileName, err);
                        },
                    });
            });
        }

        /** Swaps the "Copying image…" placeholder for the copied bytes. */
        function showImageCopy(img: HTMLImageElement, blobUrl: string, fileName: string, ref: ImageAssetRef): void {
            img.removeAttribute(FieldMergeConstants.COPY_SRC);
            img.crossOrigin = 'anonymous'; // keep the cache CORS-readable for the next copy
            img.src = blobUrl;
            imagesService.writeImageMeta(img, { fileName, ...ref });
            img.removeAttribute('data-uploading');
        }

        function markImageBroken(img: HTMLImageElement, fileName: string): void {
            // Never leave a hotlink to another play's presigned URL in the content.
            img.classList.add(FieldMergeConstants.FR_IMAGE_ERROR);
            img.setAttribute(FieldMergeConstants.FR_DATA_HAS_IMAGE_ERROR, 'true');
            img.src = imagesService.buildErrorImageSvgDataUrl(fileName);
        }
    }

    #clearFroalaCopyCache(): void {
        FieldMergeConstants.FR_COPY_KEYS.forEach(k => this.#localStorageService.removeItem(k));
    }
}
